import { type ClassValue, clsx } from "clsx";

// Simple clsx-like utility (no external dep needed for basic use)
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export function formatPrice(
  price: number | string,
  locale: string = "en-US",
  currencyCode: string = "USD",
  exchangeRate: number = 1,
  options?: { forceDecimals?: boolean }
): string {
  const num = typeof price === "string" ? parseFloat(price) : price;
  const converted = num * exchangeRate;
  // Default behavior: show decimals only when the amount has fractional
  // cents — keeps large wig prices clean ("$5,000") but preserves exact
  // small rates ("$4.79"). `forceDecimals: true` is for contexts where
  // we're showing a row of values side-by-side and mixed formatting
  // looks sloppy (checkout: "$4.79" next to "$15" should be "$15.00").
  const hasFraction = Math.round(converted * 100) % 100 !== 0;
  const showDecimals = options?.forceDecimals || hasFraction;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(converted);
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function generateOrderNumber(): string {
  const timestamp = Date.now().toString(36).toUpperCase();
  const random = Math.random().toString(36).substring(2, 5).toUpperCase();
  return `WIG-${timestamp}${random}`;
}

// NJ delivery zone zip codes
export const DELIVERY_ZONES: Record<string, string[]> = {
  Lakewood: ["08701"],
  "Toms River": ["08753", "08755", "08757"],
  Jackson: ["08527"],
};

export const ALL_DELIVERY_ZIPS = Object.values(DELIVERY_ZONES).flat();

export function isDeliveryEligible(zip: string): boolean {
  return ALL_DELIVERY_ZIPS.includes(zip);
}

export function getDeliveryArea(zip: string): string | null {
  for (const [area, zips] of Object.entries(DELIVERY_ZONES)) {
    if (zips.includes(zip)) return area;
  }
  return null;
}
